import Vue from 'vue'
import { print, visit } from 'graphql'
import { graphQlUrl } from '@/constants/config'

const marker = 'wel-support-window'
const grantKey = 'wel-support-grant'
const entry = /\/student-support\/?$/i.test(window.location.pathname)
let marked = false
let storedGrant = null
try {
  marked = window.sessionStorage.getItem(marker) === '1'
  const stored = JSON.parse(window.sessionStorage.getItem(grantKey) || 'null')
  if (stored && typeof stored.token === 'string' && stored.token && Number.isFinite(Date.parse(stored.expiresAt)) && Date.parse(stored.expiresAt) > Date.now()) storedGrant = stored
  else window.sessionStorage.removeItem(grantKey)
} catch (error) { /* Missing/malformed storage fails closed below. */ }
export const supportState = Vue.observable({
  mode: entry || marked || Boolean(storedGrant),
  active: Boolean(storedGrant),
  ready: false,
  expiresAt: storedGrant ? storedGrant.expiresAt : '',
  message: 'กรุณาเปิดจากหน้าผู้ดูแลใหม่',
  selectedContractId: storedGrant ? storedGrant.selectedContractId || '' : '',
  returnPath: '/'
})
let credential = storedGrant ? storedGrant.token : ''
let timer
let epoch = 0
const controllers = new Set()
const endListeners = new Set()

export const onSupportEnd = callback => { endListeners.add(callback); return () => endListeners.delete(callback) }
const cancelRequests = () => {
  epoch++
  controllers.forEach(controller => controller.abort())
  controllers.clear()
}
const persistGrant = () => window.sessionStorage.setItem(grantKey, JSON.stringify({
  token: credential,
  expiresAt: supportState.expiresAt,
  selectedContractId: supportState.selectedContractId
}))
export function endSupport (message = 'สิทธิ์หมดอายุ กรุณาเปิดจากหน้าผู้ดูแลใหม่') {
  credential = ''
  cancelRequests()
  supportState.active = false
  supportState.ready = false
  supportState.message = message
  supportState.selectedContractId = ''
  supportState.returnPath = '/'
  window.clearTimeout(timer)
  try { window.sessionStorage.removeItem(grantKey) } catch (error) { /* Backend expiry remains authoritative. */ }
  endListeners.forEach(callback => callback())
}
export function startSupport (grant) {
  const deadline = Date.parse(grant.expiresAt)
  if (!supportState.mode || typeof grant.token !== 'string' || !grant.token || !Number.isFinite(deadline) || deadline <= Date.now()) throw new Error('invalid_support_grant')
  // A NON-secret per-tab marker prevents refresh/navigation falling back to an existing student login.
  window.sessionStorage.setItem(marker, '1')
  credential = grant.token
  supportState.expiresAt = grant.expiresAt
  supportState.selectedContractId = grant.contractId || ''
  supportState.active = true
  supportState.ready = false
  persistGrant()
  window.clearTimeout(timer)
  timer = window.setTimeout(() => endSupport(), Math.min(deadline - Date.now(), 900000))
}
export function selectSupportContract (id) {
  cancelRequests()
  supportState.selectedContractId = id
  if (supportState.active) persistGrant()
}
export function markSupportReady () { supportState.ready = true }
export function setSupportReturnPath (path) { supportState.returnPath = path || '/' }
export function supportActive () {
  if (!supportState.active || !credential || Date.parse(supportState.expiresAt) <= Date.now()) return false
  return true
}
export async function supportQuery (document, variables = {}) {
  if (!supportActive()) { endSupport(); throw new Error('support_expired') }
  if (document.definitions.length !== 1 || document.definitions[0].operation !== 'query') throw new Error('โหมดผู้ดูแลไม่มีสิทธิ์ทำรายการนี้')
  // The normal login query contains OTP flags/values; support never requests them.
  const safeDocument = visit(document, { Field: node => ['oneTimePassword', 'oneTimePasswordReset'].includes(node.name.value) ? null : undefined })
  const requestEpoch = epoch
  const controller = new AbortController()
  controllers.add(controller)
  try {
    const response = await fetch(graphQlUrl, { method: 'POST', credentials: 'omit', cache: 'no-store', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', 'x-wel-support-token': credential },
      body: JSON.stringify({ query: print(safeDocument), variables }) })
    const payload = await response.json()
    if (requestEpoch !== epoch || !supportActive()) throw new Error('support_request_cancelled')
    // Let Apollo expose only reviewed QR validation errors, never internal errors.
    const operation = document.definitions[0]
    if (response.ok && operation.operation === 'mutation'
      && operation.selectionSet.selections[0].name.value === 'createQrPayment'
      && Array.isArray(payload.errors) && payload.errors.length
      && payload.errors.every(error => error.extensions && error.extensions.code === 'create_qr_payment_failed')) {
      return { data: null, errors: payload.errors.map(error => ({
        message: error.message,
        extensions: { code: 'create_qr_payment_failed' }
      })) }
    }
    if (!response.ok || payload.errors || !payload.data) {
      if (response.status === 401 || response.status === 403 || (payload.errors || []).some(e => e.extensions && ['support_access_denied', 'authentication_error'].includes(e.extensions.code))) endSupport('สิทธิ์ไม่พร้อมใช้งาน กรุณาเปิดจากหน้าผู้ดูแลใหม่')
      throw new Error('ไม่สามารถโหลดข้อมูลในโหมดผู้ดูแลได้')
    }
    return payload
  } finally { controllers.delete(controller) }
}
if (storedGrant) timer = window.setTimeout(() => endSupport(), Math.min(Date.parse(storedGrant.expiresAt) - Date.now(), 900000))
// Refresh destroys memory and pending requests, but the scoped grant survives in this tab only.
// Closing the tab destroys sessionStorage; explicit exit/expiry removes the grant immediately.
if (supportState.mode) window.addEventListener('pagehide', cancelRequests)
